/**
 * Supabase-Anbindung für Zusammenführen und Verschieben von Arbeitsschritten
 * (Logik: `schritteZusammenfuehren.ts`, `abschnittOrdnung.ts`).
 *
 * Alles, was eine BESTEHENDE Zeile von `sige_arbeitsschritte` schreibt oder
 * löscht, läuft über die Schreib-Warteschlange des Schritts
 * (`schrittSchreibWarteschlange.ts`) — sonst überholt es das Autospeichern
 * der Detailansicht oder wird von ihm überholt.
 *
 * Ein durch RLS gesperrtes UPDATE/DELETE meldet keinen Fehler, nur 0 Zeilen.
 * Deshalb liest jeder Schreibvorgang die betroffenen ids zurück und zählt.
 */
import { supabase } from '../../services/supabaseClient';
import { reiheSchrittAuftrag } from './schrittSchreibWarteschlange';
import type { Arbeitsschritt } from './types';
import { pruefeUnveraendert } from './schritteZusammenfuehren';
import type { OrdnungDb, ZusammenfuehrDb } from './schritteZusammenfuehren';

const leseZeile = async (id: string): Promise<Arbeitsschritt | null> => {
  const { data, error } = await supabase.from('sige_arbeitsschritte').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return (data as Arbeitsschritt | null) ?? null;
};

export const SUPABASE_ZUSAMMENFUEHR_DB: ZusammenfuehrDb = {
  // Mit `vorher` liest der Auftrag die Zeile unmittelbar vor dem Schreiben
  // noch einmal — im selben Auftrag, damit kein eigener Schreibvorgang
  // dazwischenrutscht (siehe fuehreZusammen).
  schreibeZiel: (id, felder, vorher) => reiheSchrittAuftrag(id, async (): Promise<Arbeitsschritt> => {
    if (vorher) pruefeUnveraendert(vorher, await leseZeile(id));
    const { data, error } = await supabase.from('sige_arbeitsschritte').update(felder).eq('id', id).select().single();
    if (error) throw error;
    return data as Arbeitsschritt;
  }),
  zaehleGefaehrdungen: async (ids) => {
    if (!ids.length) return 0;
    const { count, error } = await supabase.from('sige_gefaehrdungen')
      .select('id', { count: 'exact', head: true }).in('arbeitsschritt_id', ids);
    if (error) throw error;
    // Ohne Zahl nie 0 annehmen — sonst würde gelöscht, und die Gefährdungen gingen mit.
    if (typeof count !== 'number') throw new Error('Anzahl der Gefährdungen unbekannt');
    return count;
  },
  haengeGefaehrdungenUm: async (vonIds, nachId) => {
    if (!vonIds.length) return 0;
    const { data, error } = await supabase.from('sige_gefaehrdungen')
      .update({ arbeitsschritt_id: nachId }).in('arbeitsschritt_id', vonIds).select('id');
    if (error) throw error;
    return Array.isArray(data) ? data.length : 0;
  },
  loescheSchritt: (id, vorher) => reiheSchrittAuftrag(id, async (): Promise<void> => {
    if (vorher) pruefeUnveraendert(vorher, await leseZeile(id));
    const { data, error } = await supabase.from('sige_arbeitsschritte').delete().eq('id', id).select('id');
    if (error) throw error;
    if (!Array.isArray(data) || !data.length) throw new Error('Arbeitsschritt nicht gelöscht (existiert nicht mehr oder fehlende Berechtigung)');
  }),
  lese: (id) => reiheSchrittAuftrag(id, () => leseZeile(id)),
};

export const SUPABASE_ORDNUNG_DB: OrdnungDb = {
  schreibeOrdnung: (o) => reiheSchrittAuftrag(o.id, async (): Promise<void> => {
    const { data, error } = await supabase.from('sige_arbeitsschritte')
      .update({ hauptabschnitt: o.hauptabschnitt, sortierung: o.sortierung }).eq('id', o.id).select('id');
    if (error) throw error;
    if (!Array.isArray(data) || !data.length) throw new Error('Arbeitsschritt existiert nicht mehr');
  }),
};
